import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    Alert,
    Button,
    Card,
    Col,
    Empty,
    Form,
    Input,
    message,
    Modal,
    Row,
    Select,
    Space,
    Table,
    Tabs,
    Tag,
    Typography
} from 'antd';
import {
    EditOutlined,
    PlusOutlined,
    SearchOutlined,
    ShoppingOutlined
} from '@ant-design/icons';
import { connect } from 'react-redux';
import { AppState } from '../store';
import {
    getCatalogProducts,
    getServices,
    getServicesForManagement,
    postCatalogProduct,
    postService,
    putCatalogProduct,
    putService
} from '../store/user/thunks';
import {
    CatalogProduct,
    ManagedService,
    PlanService,
    CatalogProductManagementRequest
} from '../store/user/types';

const { Title, Text } = Typography;

const ProductPackageManagement: React.FC<any> = (props: any) => {
    const [productForm] = Form.useForm();
    const [serviceForm] = Form.useForm();
    const [services, setServices] = useState<PlanService[]>([]);
    const [managedServices, setManagedServices] = useState<ManagedService[]>([]);
    const [products, setProducts] = useState<CatalogProduct[]>([]);
    const [selectedServiceId, setSelectedServiceId] = useState<string>();
    const [productSearch, setProductSearch] = useState('');
    const [searchBy, setSearchBy] = useState<'description' | 'code'>('description');
    const [productModalOpen, setProductModalOpen] = useState(false);
    const [serviceModalOpen, setServiceModalOpen] = useState(false);
    const [editingProduct, setEditingProduct] = useState<CatalogProduct | null>(null);
    const [editingService, setEditingService] = useState<ManagedService | null>(null);
    const productSearchRequestId = useRef(0);

    const selectedService = useMemo(
        () => services.find(service => service.id === selectedServiceId),
        [services, selectedServiceId]
    );

    const loadServices = async () => {
        const response = await props.getServices();
        const loadedServices = response || [];
        setServices(loadedServices);
    };

    const loadManagedServices = async () => {
        const response = await props.getServicesForManagement();
        setManagedServices(response || []);
    };

    const openServiceManagement = async () => {
        setServiceModalOpen(true);
        setEditingService(null);
        serviceForm.resetFields();
        serviceForm.setFieldsValue({ status: 'ACTIVO' });
        await loadManagedServices();
    };

    const editService = (service: ManagedService) => {
        setEditingService(service);
        serviceForm.setFieldsValue({
            name: service.name,
            description: service.description || '',
            observation: service.observation || '',
            category: service.category,
            resourceType: service.resourceType || '',
            status: service.status
        });
    };

    const newService = () => {
        setEditingService(null);
        serviceForm.resetFields();
        serviceForm.setFieldsValue({ status: 'ACTIVO' });
    };

    const saveService = async () => {
        const values = await serviceForm.validateFields();
        const request: ManagedService = {
            id: editingService?.id || null,
            name: values.name,
            description: values.description || null,
            observation: values.observation || null,
            category: values.category,
            resourceType: values.resourceType || null,
            status: values.status
        };
        const response = editingService
            ? await props.putService(request)
            : await props.postService(request);

        if (response) {
            await Promise.all([loadServices(), loadManagedServices()]);
            if (response.status === 'ACTIVO') setSelectedServiceId(response.id);
            newService();
        }
    };

    useEffect(() => {
        loadServices();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (selectedServiceId) {
            setProducts([]);
            setProductSearch('');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedServiceId]);

    const searchProducts = async (
        value = productSearch,
        type: 'description' | 'code' = searchBy,
        updateTable = true
    ) => {
        if (!value.trim()) {
            message.warning('Ingrese un texto para buscar productos');
            return [];
        }

        const requestId = ++productSearchRequestId.current;
        const response = await props.getCatalogProducts(selectedServiceId, type, value.trim());
        if (requestId !== productSearchRequestId.current) {
            return [];
        }

        const loadedProducts = response || [];
        if (updateTable) setProducts(loadedProducts);
        return loadedProducts;
    };

    const openProductModal = (product?: CatalogProduct) => {
        const serviceId = product?.serviceId || selectedServiceId;
        if (!serviceId) {
            message.warning('Seleccione un servicio');
            return;
        }
        setSelectedServiceId(serviceId);
        setEditingProduct(product || null);
        productForm.resetFields();
        productForm.setFieldsValue({
            code: product?.code || '',
            description: product?.description || '',
            genericDescription: product?.genericDescription || '',
            commercialName: product?.commercialName || '',
            status: product?.status || 'ACTIVO'
        });
        setProductModalOpen(true);
    };

    const saveProduct = async () => {
        const values = await productForm.validateFields();
        const request: CatalogProductManagementRequest = {
            id: editingProduct?.id || null,
            serviceId: selectedServiceId!,
            code: values.code || null,
            description: values.description,
            genericDescription: values.genericDescription,
            commercialName: values.commercialName,
            status: values.status || 'ACTIVO'
        };
        const response = editingProduct
            ? await props.putCatalogProduct(request)
            : await props.postCatalogProduct(request);
        if (response) {
            setProductModalOpen(false);
            setEditingProduct(null);
            productForm.resetFields();
            setProductSearch(response.code || values.description);
            setSearchBy(response.code ? 'code' : 'description');
            await searchProducts(
                response.code || values.description,
                response.code ? 'code' : 'description'
            );
        }
    };

    const productColumns = [
        {
            title: 'Código',
            dataIndex: 'code',
            key: 'code',
            width: 150,
            render: (value: string) => <Tag color="geekblue">{value}</Tag>
        },
        {
            title: 'Producto',
            dataIndex: 'description',
            key: 'description',
            render: (value: string, row: CatalogProduct) => (
                <Space direction="vertical" size={0}>
                    <Text strong>{value}</Text>
                    {row.commercialName !== value
                        ? <Text type="secondary">{row.commercialName}</Text>
                        : null}
                </Space>
            )
        },
        {
            title: 'Servicio',
            dataIndex: 'service',
            key: 'service',
            width: 180,
            render: (value: string, row: CatalogProduct) => (
                <Space direction="vertical" size={0}>
                    <Text>{value}</Text>
                    <Text type="secondary">{row.serviceCategory}</Text>
                </Space>
            )
        },
        {
            title: 'Estado',
            dataIndex: 'status',
            key: 'status',
            width: 110,
            render: (value: string) => (
                <Tag color={value === 'ACTIVO' ? 'green' : 'red'}>{value}</Tag>
            )
        },
        {
            title: 'Acciones',
            key: 'action',
            width: 130,
            render: (_: unknown, row: CatalogProduct) => (
                <Button
                    size="small"
                    icon={<EditOutlined />}
                    onClick={() => openProductModal(row)}
                >
                    Editar
                </Button>
            )
        }
    ];

    const managedServiceColumns = [
        {
            title: 'Servicio',
            dataIndex: 'name',
            key: 'name',
            render: (value: string, row: ManagedService) => (
                <Space direction="vertical" size={0}>
                    <Text strong>{value}</Text>
                    <Text type="secondary">{row.category}</Text>
                </Space>
            )
        },
        {
            title: 'Estado',
            dataIndex: 'status',
            key: 'status',
            width: 100,
            render: (value: string) => (
                <Tag color={value === 'ACTIVO' ? 'green' : 'red'}>{value}</Tag>
            )
        },
        {
            title: '',
            key: 'action',
            width: 54,
            render: (_: unknown, row: ManagedService) => (
                <Button size="small" icon={<EditOutlined />} onClick={() => editService(row)} />
            )
        }
    ];

    return (
        <div className="tbc-admin-page">
            <Space direction="vertical" size={18} style={{ width: '100%' }}>
                <div>
                    <Title level={3} style={{ marginBottom: 4 }}>Catálogo global de servicios y productos</Title>
                    <Text type="secondary">
                        Blue administra aquí únicamente los registros globales de str.service y pdt.products.
                    </Text>
                </div>

                <Alert
                    type="info"
                    showIcon
                    message="Configuración global de Blue"
                    description="Los productos de esta pantalla no pertenecen a una organización. La asociación, precio, cobertura, copago y personalización de cada cliente se gestiona posteriormente en organization_product."
                />

                <Card
                    title={
                        <Space direction="vertical" size={0}>
                            <Text strong>Selecciona qué deseas administrar</Text>
                        </Space>
                    }
                >
                    <Tabs
                        defaultActiveKey="products"
                        items={[
                        {
                            key: 'products',
                            label: <span><ShoppingOutlined /> Productos globales</span>,
                            children: (
                                <Card
                                    title={selectedService ? `Productos globales · ${selectedService.name}` : 'Productos globales'}
                                    bordered={false}
                                    extra={
                                        <Space>
                                            <Button icon={<EditOutlined />} onClick={openServiceManagement}>
                                                Gestionar servicios
                                            </Button>
                                            <Button type="primary" icon={<PlusOutlined />} onClick={() => openProductModal()}>
                                                Nuevo producto
                                            </Button>
                                        </Space>
                                    }
                                >
                                    <Row gutter={[8, 8]} style={{ marginBottom: 16 }}>
                                        <Col xs={24} md={8}>
                                            <Select
                                                allowClear
                                                showSearch
                                                optionFilterProp="label"
                                                style={{ width: '100%' }}
                                                placeholder="Todos los servicios"
                                                value={selectedServiceId}
                                                onChange={setSelectedServiceId}
                                                options={services.map(service => ({
                                                    value: service.id,
                                                    label: `${service.name} · ${service.category || 'Sin categoría'}`
                                                }))}
                                            />
                                        </Col>
                                        <Col xs={24} md={4}>
                                            <Select
                                                style={{ width: '100%' }}
                                                value={searchBy}
                                                onChange={setSearchBy}
                                                options={[
                                                    { value: 'description', label: 'Descripción' },
                                                    { value: 'code', label: 'Código' }
                                                ]}
                                            />
                                        </Col>
                                        <Col xs={24} md={8}>
                                            <Input
                                                value={productSearch}
                                                placeholder="Buscar productos"
                                                onChange={event => setProductSearch(event.target.value)}
                                                onPressEnter={() => searchProducts()}
                                            />
                                        </Col>
                                        <Col xs={24} md={4}>
                                            <Button
                                                block
                                                type="primary"
                                                icon={<SearchOutlined />}
                                                onClick={() => searchProducts()}
                                            >
                                                Buscar
                                            </Button>
                                        </Col>
                                    </Row>
                                    <Table
                                        rowKey="id"
                                        columns={productColumns}
                                        dataSource={products}
                                        pagination={{ pageSize: 10 }}
                                        locale={{ emptyText: <Empty description="Busque productos por código o descripción" /> }}
                                    />
                                </Card>
                            )
                        }
                        ]}
                    />
                </Card>
            </Space>

            <Modal
                open={serviceModalOpen}
                title="Gestionar servicios"
                footer={null}
                width={980}
                onCancel={() => setServiceModalOpen(false)}
                destroyOnClose
            >
                <Row gutter={[20, 20]}>
                    <Col xs={24} lg={13}>
                        <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 12 }}>
                            <Text strong>Servicios registrados</Text>
                            <Button type="primary" icon={<PlusOutlined />} onClick={newService}>
                                Nuevo servicio
                            </Button>
                        </Space>
                        <Table
                            rowKey="id"
                            size="small"
                            columns={managedServiceColumns}
                            dataSource={managedServices}
                            pagination={{ pageSize: 6 }}
                        />
                    </Col>
                    <Col xs={24} lg={11}>
                        <Card
                            size="small"
                            title={editingService ? `Editar: ${editingService.name}` : 'Crear servicio'}
                        >
                            <Form form={serviceForm} layout="vertical">
                                <Form.Item
                                    name="name"
                                    label="Nombre"
                                    rules={[{ required: true, whitespace: true, message: 'Ingrese el nombre' }]}
                                >
                                    <Input maxLength={150} />
                                </Form.Item>
                                <Form.Item
                                    name="category"
                                    label="Categoría"
                                    rules={[{ required: true, whitespace: true, message: 'Ingrese la categoría' }]}
                                >
                                    <Input maxLength={120} placeholder="Ej. LABORATORIO" />
                                </Form.Item>
                                <Form.Item
                                    name="resourceType"
                                    label="Tipo de recurso"
                                    extra="Clasifica el servicio global, por ejemplo SERVICIOS_CLINICOS o CAPACIDADES_CLINICAS."
                                >
                                    <Input maxLength={255} placeholder="Ej. SERVICIOS_CLINICOS" />
                                </Form.Item>
                                <Form.Item name="description" label="Descripción">
                                    <Input.TextArea rows={2} maxLength={255} />
                                </Form.Item>
                                <Form.Item name="observation" label="Observación">
                                    <Input maxLength={255} />
                                </Form.Item>
                                <Form.Item name="status" label="Estado" rules={[{ required: true }]}>
                                    <Select
                                        options={[
                                            { value: 'ACTIVO', label: 'Activo' },
                                            { value: 'INACTIVO', label: 'Inactivo' }
                                        ]}
                                    />
                                </Form.Item>
                                <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
                                    {editingService ? <Button onClick={newService}>Cancelar edición</Button> : null}
                                    <Button type="primary" onClick={saveService}>
                                        {editingService ? 'Guardar cambios' : 'Crear servicio'}
                                    </Button>
                                </Space>
                            </Form>
                        </Card>
                    </Col>
                </Row>
            </Modal>

            <Modal
                open={productModalOpen}
                title={editingProduct ? 'Editar producto del catálogo global' : 'Nuevo producto del catálogo global'}
                okText="Guardar"
                cancelText="Cancelar"
                onOk={saveProduct}
                onCancel={() => {
                    setProductModalOpen(false);
                    setEditingProduct(null);
                }}
                destroyOnClose
            >
                <Form form={productForm} layout="vertical">
                    <Form.Item label="Servicio">
                        <Input value={selectedService?.name} disabled />
                    </Form.Item>
                    <Form.Item
                        name="code"
                        label="Código global"
                        extra="Puede dejarlo vacío para generar un código automáticamente dentro del servicio."
                    >
                        <Input maxLength={50} />
                    </Form.Item>
                    <Form.Item
                        name="description"
                        label="Descripción"
                        rules={[{ required: true, whitespace: true, message: 'Ingrese la descripción' }]}
                    >
                        <Input maxLength={255} />
                    </Form.Item>
                    <Form.Item
                        name="genericDescription"
                        label="Descripción genérica"
                        rules={[{ required: true, whitespace: true, message: 'Ingrese la descripción genérica' }]}
                    >
                        <Input maxLength={255} />
                    </Form.Item>
                    <Form.Item
                        name="commercialName"
                        label="Nombre comercial"
                        rules={[{ required: true, whitespace: true, message: 'Ingrese el nombre comercial' }]}
                    >
                        <Input maxLength={255} />
                    </Form.Item>
                    {editingProduct ? (
                        <Form.Item name="status" label="Estado" rules={[{ required: true }]}>
                            <Select
                                options={[
                                    { value: 'ACTIVO', label: 'Activo' },
                                    { value: 'INACTIVO', label: 'Inactivo' }
                                ]}
                            />
                        </Form.Item>
                    ) : null}
                </Form>
            </Modal>

        </div>
    );
};

const mapStateToProps = (state: AppState) => ({
    userAction: state.userAction
});

export default connect(mapStateToProps, {
    getServices,
    getServicesForManagement,
    postService,
    putService,
    getCatalogProducts,
    postCatalogProduct,
    putCatalogProduct
})(ProductPackageManagement);
